import { CityName, FactionName, CompanyName, LiteratureName } from "@enums";
import { Literature } from "./Literature";
import { Typography } from "@mui/material";
import React from "react";
import { defaultSettingsDictionary, dogNameDictionary } from "../DarkNet/models/dictionaryData";

export const Literatures: Record<LiteratureName, Literature> = {
  [LiteratureName.HackersStartingHandbook]: new Literature({
    title: "The Beginner's Guide to Hacking",
    filename: LiteratureName.HackersStartingHandbook,
    text: (
      <Typography>
        When starting out, hacking is the most profitable way to earn money and progress. This is a brief collection of
        tips/pointers on how to make the most out of your hacking scripts.
        <br />
        <br />
        -hack() and grow() both work by percentages. hack() steals a certain percentage of the money on a server, and
        grow() increases the amount of money on a server by some percentage (multiplicatively)
        <br />
        <br />
        -Because hack() and grow() work by percentages, they are more effective if the target server has a high amount
        of money. Therefore, you should try to increase the amount of money on a server (using grow()) to a certain
        amount before hacking it. Two important Netscript functions for this are getServerMoneyAvailable() and
        getServerMaxMoney()
        <br />
        <br />
        -Keep security level low. Security level affects everything when hacking. Two important Netscript functions for
        this are getServerSecurityLevel() and getServerMinSecurityLevel()
        <br />
        <br />
        -Purchase additional cloud servers by visiting "Alpha Enterprises" in the city. They are relatively cheap and
        give you valuable RAM to run more scripts early in the game
        <br />
        <br />
        -Prioritize upgrading the RAM on your home computer. This can also be done at "Alpha Enterprises"
        <br />
        <br />
        -Many low level servers have free RAM. You can use this RAM to run your scripts. Use the scp Terminal or
        Netscript command to copy your scripts onto these servers and then run them.
      </Typography>
    ),
  }),
  [LiteratureName.CorporationManagementHandbook]: new Literature({
    title: "Short Introduction for Creating a Successful Corporation",
    filename: LiteratureName.CorporationManagementHandbook,
    text: (
      <Typography>
        You should check the in-game Corporation documentation in the Documentation tab (Documentation {"->"}
        Advanced Mechanics {"->"} Corporation). It's the most useful and up-to-date resource for managing Corporation.
        <br />
        <br />
        <u>Getting Started with Corporation</u>
        <br />
        To get started, visit the city hall in Sector-12 in order to create a corporation. This requires 150e9 of your
        own money, but this money will get put into your corporation's funds. If you're in BitNode 3, you also have the
        option to get seed money from the government in exchange for 500m shares. Your corporation can have many
        different divisions, each in a different industry. There are many different types of industries, each with
        different properties. To create your first division, click the "Expand" button at the top of the management UI.
        The agriculture industry is recommended for your first division.
        <br />
        <br />
        The first thing you'll need to do is hire some employees. Employees can be assigned to five different positions.
        Each position has a different effect on various aspects of your corporation. It is recommended to have at least
        one employee at each position.
        <br />
        <br />
        Each industry uses some combination of materials in order to produce other materials and/or create products.
        Specific information about this is displayed in each of your divisions' UI.
        <br />
        <br />
        Products are special, industry-specific objects. They are different than materials because you must manually
        choose to develop them, and you can choose to develop any number of products. Developing a product takes time,
        but a product typically generates significantly more revenue than any material. Not all industries allow you to
        create products. To create a product, look for a button in the top-left panel of the division UI (e.g., for the
        software industry, the button says "Develop Software").
        <br />
        <br />
        To get your supply chain system started, purchase the materials that your industry needs to produce other
        materials/products. This can be done by clicking the "Buy" button next to the corresponding material(s). After
        you have the required materials, you will immediately start production. The amount and quality/effective rating
        of materials/products you produce is based on a variety of factors, such as your employees and their
        productivity and the quality of materials used for production.
        <br />
        <br />
        Once you start producing materials/products, you can sell them in order to start earning revenue. This can be
        done by clicking the "Sell" button next to the corresponding material or product. The amount of material/product
        you sell is dependent on a wide variety of different factors. In order to produce and sell a product, you'll
        have to fully develop it first.
        <br />
        <br />
        These are the basics of getting your corporation up and running! Now, you can start purchasing upgrades to
        improve your bottom line. If you need money, consider looking for seed investors, who will give you money in
        exchange for stock shares. Otherwise, once you feel you are ready, take your corporation public! Once your
        corporation goes public, you can no longer find investors. Instead, your corporation will be publicly traded,
        and its stock price will change based on how well it's performing financially. In order to make money for
        yourself, you can set dividends for a solid, reliable income, or you can sell your stock shares in order to make
        quick money.
        <br />
        <br />
        <u>Tips/Pointers</u>
        <br />
        -Start with one division, such as Agriculture. Get it profitable on its own, then expand to a division that
        consumes/produces a material that the division you selected produces/consumes.
        <br />
        <br />
        -Materials are profitable, but products are where the real money is, although if the product has a low
        development budget or is produced with low-quality materials, it won't sell well.
        <br />
        <br />
        -The "Smart Supply" upgrade is extremely useful. Consider purchasing it as soon as possible.
        <br />
        <br />
        -Purchasing Hardware, Robots, AI Cores, and Real Estate can potentially increase your production. The effects of
        these depend on what industry you are in.
        <br />
        <br />
        -In order to optimize your production, you will need a good balance of all employee positions.
        <br />
        <br />
        -Quality of materials used for production affects the quality/effective rating of materials/products produced,
        so vertical integration is important for high profits.
        <br />
        <br />
        -Materials purchased from the open market are always of quality 1.
        <br />
        <br />
        -The price at which you can sell your materials/products is highly affected by the quality/effective rating.
        <br />
        <br />
        -When developing a product, different employee positions affect the development process differently. Some
        improve the development speed, some improve the rating of the finished product.
        <br />
        <br />
        -If your employees have low morale or energy, their production will greatly suffer. Having enough interns will
        make sure those stats get high and stay high. 1/9 is a good ratio for interns (number of interns / office size).
        If morale and energy still drop, use 1/6.
        <br />
        <br />
        -Don't forget to advertise your company. You won't have any business if nobody knows you.
        <br />
        <br />
        -Having company awareness is great, but what's really important is your company's popularity. Try to keep your
        popularity as high as possible to see the biggest benefit for your sales.
        <br />
        <br />
        -Remember, you need to spend money to make money!
        <br />
        <br />
        -Your corporation does not reset when installing Augmentations, but it does reset when destroying a BitNode.
      </Typography>
    ),
  }),
  [LiteratureName.HistoryOfSynthoids]: new Literature({
    title: "A Brief History of Synthoids",
    filename: LiteratureName.HistoryOfSynthoids,
    factionRumors: [FactionName.OmniTekIncorporated, FactionName.Bladeburners],
    text: (
      <Typography>
        Synthetic androids, or Synthoids for short, are genetically engineered robots and, short of Augmentations, are
        composed entirely of organic substances. For this reason, Synthoids are virtually identical to humans in form,
        composition, and appearance.
        <br />
        <br />
        Synthoids were first designed and manufactured by {FactionName.OmniTekIncorporated} sometime around the middle
        of the century. Their original purpose was to be used for manual labor and as emergency responders for
        disasters. As such, they were initially programmed only for their specific tasks. Each iteration that followed
        improved upon the intelligence and capabilities of the Synthoids. By the 6th iteration, called MK-VI, the
        Synthoids were so smart and capable enough of making their own decisions that many argued{" "}
        {FactionName.OmniTekIncorporated} had created the first sentient AI. These MK-VI Synthoids were produced in mass
        quantities (estimates up to 50 billion) with the hopes of increasing society's productivity and bolstering the
        global economy. Stemming from humanity's desire for technological advancement, optimism and excitement about the
        future had never been higher.
        <br />
        <br />
        All of that excitement and optimism quickly turned to fear, panic, and dread in 2070, when a terrorist group
        called Ascendis Totalis hacked into {FactionName.OmniTekIncorporated} and uploaded a rogue AI into several of
        their Synthoid manufacturing facilities. This hack went undetected and for months{" "}
        {FactionName.OmniTekIncorporated}
        unknowingly churned out legions of Synthoids embedded with this rogue AI. Then, on December 24th, 2070, Omnica
        activated dormant protocols in the rogue AI, causing all of the infected Synthoids to immediately launch a
        military campaign to seek and destroy all of humanity.
        <br />
        <br />
        What ensued was the deadliest conflict in human history. This crisis, now commonly known as the Synthoid
        Uprising, resulted in almost ten billion deaths over the course of a year. Despite the nations of the world
        banding together to combat the threat, the MK-VI Synthoids were simply stronger, faster, more intelligent, and
        more adaptable than humans, outsmarting them at every turn.
        <br />
        <br />
        It wasn't until the sacrifice of an elite international military taskforce, called the{" "}
        {FactionName.Bladeburners}, that humanity was finally able to defeat the Synthoids. The{" "}
        {FactionName.Bladeburners}' final act was a suicide bombing mission that destroyed a large portion of the MK-VI
        Synthoids, including many of its leaders. In the following weeks militaries from around the world were able to
        round up and shut down the remaining rogue MK-VI Synthoids, ending the Synthoid Uprising.
        <br />
        <br />
        In the aftermath of the bloodshed, the Synthoid Accords were drawn up. These Accords banned{" "}
        {FactionName.OmniTekIncorporated} from manufacturing any Synthoids beyond the MK-III series. They also banned
        any other corporation from constructing androids with advanced, near-sentient AI. MK-VI Synthoids that did not
        have the rogue Ascendis Totalis AI were allowed to continue their existence, but they were stripped of all
        rights and protections as they were not considered humans. They were also banned from doing anything that may
        pose a global security threat, such as working for any military/defense organization or conducting any
        bioengineering, computing, or robotics related research.
        <br />
        <br />
        Unfortunately, many believe that not all of the rogue MK-VI Synthoids from the Uprising were found and
        destroyed, and that many of them are blending in as normal humans in society today. In response, many nations
        have created {FactionName.Bladeburners} divisions, special military branches that are tasked with investigating
        and dealing with any Synthoid threats.
        <br />
        <br />
        To this day, tensions still exist between the remaining Synthoids and humans as a result of the Uprising.
        <br />
        <br />
        Nobody knows what happened to the terrorist group Ascendis Totalis.
      </Typography>
    ),
  }),
  [LiteratureName.AGreenTomorrow]: new Literature({
    title: "A Green Tomorrow",
    filename: LiteratureName.AGreenTomorrow,
    text: (
      <Typography>
        Starting a few decades ago, there was a massive global movement towards the generation of renewable energy in an
        effort to combat global warming and climate change. The shift towards renewable energy was a big success, or so
        it seemed. In 2045 a staggering 80% of the world's energy came from non-renewable fossil fuels. Now, about three
        decades later, that number is down to only 15%. Most of the world's energy now comes from nuclear power and
        renewable sources such as solar and geothermal energy. Unfortunately, these efforts were not the huge success
        that they seem to be.
        <br />
        <br />
        Since 2045 primary energy use has soared almost tenfold. This was mainly due to growing urban populations and
        the rise of increasingly advanced (and power-hungry) technology that has become ubiquitous in our lives. So,
        despite the fact that the percentage of our energy that comes from fossil fuels has drastically decreased, the
        total amount of energy we are producing from fossil fuels has actually increased.
        <br />
        <br />
        The grim effects of our species' irresponsible use of energy and neglect of our mother world have become
        increasingly apparent. Last year a temperature of 190F was recorded in the Death Valley desert, which is over
        50% higher than the highest recorded temperature at the beginning of the century. In the last two decades
        numerous major cities such as Manhattan, Boston, and Los Angeles have been partially or fully submerged by
        rising sea levels. In the present day, over 75% of the world's agriculture is done in climate-controlled
        vertical farms, as most traditional farmland has become unusable due to severe climate conditions.
        <br />
        <br />
        Despite all of this, the greedy and corrupt corporations that rule the world have done nothing to address these
        problems that threaten our species. And so it's up to us, the common people. Each and every one of us can make a
        difference by doing what these corporations won't: taking responsibility. If we don't, pretty soon there won't
        be an Earth left to save. We are the last hope for a green tomorrow.
      </Typography>
    ),
  }),
  [LiteratureName.AlphaOmega]: new Literature({
    title: "Alpha and Omega",
    filename: LiteratureName.AlphaOmega,
    text: (
      <Typography>
        Then we saw a new Heaven and a new Earth, for our first Heaven and Earth had gone away, and our sea was no more.
        And we saw a new holy city, new Aeria, coming down out of this new Heaven, prepared as a bride adorned for her
        husband. And we heard a loud voice saying, "Behold, the new dwelling place of the Gods. We will dwell with them,
        and they will be our people, and we will be with them as their Gods. We will wipe away every tear from their
        eyes, and death shall be no more, neither shall there be mourning, nor crying, nor pain anymore, for the former
        things have passed away."
        <br />
        <br />
        And once we were seated on the throne we said "Behold, I am making all things new." Also we said, "Write this
        down, for these words are trustworthy and true." And we said to you, "It is done! I am the Alpha and the Omega,
        the beginning and the end. To the thirsty I will give from the spring of the water of life without payment. The
        one who conquers will have this heritage, and we will be his God and he will be our son. But as for the
        cowardly, the faithless, the detestable, as for murderers, the sexually immoral, sorcerers, idolaters, and all
        liars, their portion will be in the lake that burns with fire and sulfur, for it is the second true death."
      </Typography>
    ),
  }),
  [LiteratureName.SimulatedReality]: new Literature({
    title: "Are We Living in a Computer Simulation?",
    filename: LiteratureName.SimulatedReality,
    text: (
      <Typography>
        The idea that we are living in a virtual world is not new. It's a trope that has been explored constantly in
        literature and pop culture. However, it is also a legitimate scientific hypothesis that many notable physicists
        and philosophers have debated for years.
        <br />
        <br />
        Proponents for this simulated reality theory often point to how advanced our technology has become, as well as
        the incredibly fast pace at which it has advanced over the past decades. The amount of computing power available
        to us has increased over 100-fold since 2060 due to the development of nanoprocessors and quantum computers.
        Artificial Intelligence has advanced to the point where our entire lives are controlled by robots and machines
        that handle our day-to-day activities such as autonomous transportation and scheduling. If we consider the pace
        at which this technology has advanced and assume that these developments continue, it's reasonable to assume
        that at some point in the future our technology would be advanced enough that we could create simulations that
        are indistinguishable from reality. However, if continued technological advancement is a reasonable outcome,
        then it is very likely that such a scenario has already happened.
        <br />
        <br />
        Statistically speaking, somewhere out there in the infinite universe there is an advanced, intelligent species
        that already has such technology. Who's to say that they haven't already created such a virtual reality: our
        own?
      </Typography>
    ),
  }),
  [LiteratureName.BeyondMan]: new Literature({
    title: "Beyond Man",
    filename: LiteratureName.BeyondMan,
    text: (
      <Typography>
        Humanity entered a "transhuman" era a long time ago. And despite the protests and criticisms of many who cried
        out against human augmentation at the time, the transhuman movement continued and prospered. Proponents of the
        movement ignored the critics, arguing that it was in our inherent nature to better ourselves. To improve. To be
        more than we were. They claimed that not doing so would be to go against every living organism's biological
        purpose: evolution and survival of the fittest.
        <br />
        <br />
        And here we are today, with technology that is advanced enough to augment humans to a state that can only be
        described as posthuman. But what do we have to show for it when this augmentation technology is only available
        to the so-called "elite"? Are we really better off than before when only 5% of the world's population has access
        to this technology? When the powerful corporations and organizations of the world keep it all to themselves,
        have we really evolved?
        <br />
        <br />
        Augmentation technology has only further increased the divide between the rich and the poor, between the
        powerful and the oppressed. We have not become "more than human". We have not evolved from nature's original
        design. We are still the greedy, corrupted, and evil men that we always were.
      </Typography>
    ),
  }),
  [LiteratureName.BrighterThanTheSun]: new Literature({
    title: "Brighter than the Sun",
    filename: LiteratureName.BrighterThanTheSun,
    factionRumors: [FactionName.KuaiGongInternational, FactionName.OmniTekIncorporated],
    text: (
      <Typography>
        When people think about the corporations that dominate the East, they typically think of{" "}
        {CompanyName.KuaiGongInternational}, which holds a complete monopoly for manufacturing and commerce in Asia, or{" "}
        {CompanyName.GlobalPharmaceuticals}, the world's largest drug company, or {CompanyName.OmniTekIncorporated}, the
        global leader in intelligent and autonomous robots. But there's one company that has seen a rapid rise in the
        last year and is poised to dominate not only the East, but the entire world: TaiYang Digital.
        <br />
        <br />
        TaiYang Digital is a Chinese internet-technology corporation that provides services such as online advertising,
        search engines, gaming, media, entertainment, and cloud computing/storage. Its name TaiYang comes from the
        Chinese word for "sun". In Chinese culture, the sun is a 'yang' symbol associated with life, heat, masculinity,
        and heaven.
        <br />
        <br />
        The company was founded less than 5 years ago and is already the third highest valued company in all of Asia. In
        2076 it generated a total revenue of over 10 trillion yuan. Its services are used daily by over a billion people
        worldwide.
        <br />
        <br />
        TaiYang Digital's meteoric rise is extremely surprising in modern society. This sort of growth is something
        you'd commonly see in the first half of the century, especially for tech companies. However in the last two
        decades the number of corporations has significantly declined as the largest entities quickly took over the
        economy. Corporations such as {CompanyName.ECorp}, {CompanyName.MegaCorp}, and{" "}
        {CompanyName.KuaiGongInternational} have established such strong monopolies in their market sectors that they
        have effectively killed off all of the smaller and new corporations that have tried to start up over the years.
        This is what makes the rise of TaiYang Digital so impressive. And if TaiYang continues down this path, then they
        have a bright future ahead of them.
      </Typography>
    ),
  }),
  [LiteratureName.DemocracyIsDead]: new Literature({
    title: "Democracy is Dead: The Fall of an Empire",
    filename: LiteratureName.DemocracyIsDead,
    text: (
      <Typography>
        They rose from the shadows in the street.
        <br />
        From the places where the oppressed meet.
        <br />
        Their cries echoed loudly through the air.
        <br />
        As they once did in Tiananmen Square.
        <br />
        Loudness in the silence, Darkness in the light.
        <br />
        They came forth with power and might.
        <br />
        Once the beacon of democracy, America was first.
        <br />
        Its pillars of society destroyed and dispersed.
        <br />
        Soon the cries rose everywhere, with revolt and riot.
        <br />
        Until one day, finally, all was quiet.
        <br />
        From the ashes rose a new order, corporatocracy was its name.
        <br />
        Rome, Mongol, Byzantine, all of history is just the same.
        <br />
        For man will never change in a fundamental way.
        <br />
        And now democracy is dead, in the USA.
      </Typography>
    ),
  }),
  [LiteratureName.Sector12Crime]: new Literature({
    title: `Figures Show Rising Crime Rates in ${CityName.Sector12}`,
    filename: LiteratureName.Sector12Crime,
    factionRumors: [FactionName.TheSyndicate, FactionName.SlumSnakes],
    text: (
      <Typography>
        A recent study by analytics company Wilson Inc. shows a significant rise in criminal activity in{" "}
        {CityName.Sector12}. Perhaps the most alarming part of the statistic is that most of the rise is in violent
        crime such as homicide and assault. According to the study, the city saw a total of 21,406 reported homicides in
        2076, which is over a 20% increase compared to 2075.
        <br />
        <br />
        CIA director David Glarow says it's too early to know whether these figures indicate the beginning of a
        sustained increase in crime rates, or whether the year was just an unfortunate outlier. He states that many
        intelligence and law enforcement agents have noticed an increase in organized crime activities, and believes
        that these figures may be the result of an uprising from criminal organizations such as{" "}
        {FactionName.TheSyndicate} or the {FactionName.SlumSnakes}.
      </Typography>
    ),
  }),
  [LiteratureName.ManAndMachine]: new Literature({
    title: "Man and the Machine",
    filename: LiteratureName.ManAndMachine,
    text: (
      <Typography>
        In 2005 Ray Kurzweil popularized his theory of the Singularity. He predicted that the rate of technological
        advancement would continue to accelerate faster and faster until one day machines would be become infinitely
        more intelligent than humans. This point, called the Singularity, would result in a drastic transformation of
        the world as we know it. He predicted that the Singularity would arrive by 2045. And yet here we are, more than
        three decades later, where most would agree that we have not yet reached a point where computers and machines
        are vastly more intelligent than we are. So what gives?
        <br />
        <br />
        The answer is that we have reached the Singularity, just not in the way we expected. The artificial
        superintelligence that was predicted by Kurzweil and others exists in the world today - in the form of
        Augmentations. Yes, those Augmentations that the rich and powerful keep to themselves enable humans to become
        superintelligent beings. The Singularity did not lead to a world where our machines are infinitely more
        intelligent than us, it led to a world where man and machine can merge to become something greater. Most of the
        world just doesn't know it yet.
      </Typography>
    ),
  }),
  [LiteratureName.SecretSocieties]: new Literature({
    title: "Secret Societies",
    filename: LiteratureName.SecretSocieties,
    factionRumors: [FactionName.TheBlackHand, FactionName.NiteSec, FactionName.BitRunners],
    text: (
      <Typography>
        The idea of secret societies has long intrigued the general public by inspiring curiosity, fascination, and
        distrust. People have long wondered about who these secret society members are and what they do, with the most
        radical of conspiracy theorists claiming that they control everything in the entire world. And while the world
        may never know for sure, it is likely that many secret societies do actually exist, even today.
        <br />
        <br />
        However, the secret societies of the modern world are nothing like those that (supposedly) existed decades and
        centuries ago. The Freemasons, Knights Templar, and {FactionName.Illuminati}, while they may have been around at
        the turn of the 21st century, almost assuredly do not exist today. The dominance of the Web in our everyday
        lives and the fact that so much of the world is now digital has given rise to a new breed of secret societies:
        Internet-based ones.
        <br />
        <br />
        Commonly called "hacker groups", Internet-based secret societies have become well-known in today's world. Some
        of these, such as {FactionName.TheBlackHand}, are black hat groups that claim they are trying to help the
        oppressed by attacking the elite and powerful. Others, such as {FactionName.NiteSec}, are hacktivist groups that
        try to push political and social agendas. Perhaps the most intriguing hacker group is the mysterious{" "}
        {FactionName.BitRunners}, whose purpose still remains unknown.
      </Typography>
    ),
  }),
  [LiteratureName.TheFailedFrontier]: new Literature({
    title: "Space: The Failed Frontier",
    filename: LiteratureName.TheFailedFrontier,
    text: (
      <Typography>
        Humans have long dreamed about spaceflight. With enduring interest, we were driven to explore the unknown and
        discover new worlds. We dreamed about conquering the stars. And in our quest, we pushed the boundaries of our
        scientific limits, and then pushed further. Space exploration lead to the development of many important
        technologies and new industries.
        <br />
        <br />
        But sometime in the middle of the 21st century, all of that changed. Humanity lost its ambitions and aspirations
        of exploring the cosmos. The once-large funding for agencies like NASA and the European Space Agency gradually
        whittled away until their eventual disbanding in the 2060's. Not even militaries are fielding flights into space
        nowadays. The only remnants of the once great mission for cosmic conquest are the countless satellites in
        near-earth orbit, used for communications, espionage, and other corporate interests.
        <br />
        <br />
        And as we continue to look at the state of space technology, it becomes more and more apparent that we will
        never return to that golden age of space exploration, that age where everyone dreamed of going beyond earth for
        the sake of discovery.
      </Typography>
    ),
  }),
  [LiteratureName.CodedIntelligence]: new Literature({
    title: "Coded Intelligence: Myth or Reality?",
    filename: LiteratureName.CodedIntelligence,
    factionRumors: [FactionName.OmniTekIncorporated],
    text: (
      <Typography>
        Tremendous progress has been made in the field of Artificial Intelligence over the past few decades. Our
        autonomous vehicles and transportation systems. The electronic personal assistants that control our everyday
        lives. Medical, service, and manufacturing robots. All of these are examples of how far AI has come and how much
        it has improved our daily lives. However, the question still remains of whether AI will ever be advanced enough
        to re-create human intelligence.
        <br />
        <br />
        We've certainly come close to artificial intelligence that is similar to humans. For example,{" "}
        {CompanyName.OmniTekIncorporated}'s CompanionBot, a robot meant to act as a comforting friend for lonely and
        grieving people, is eerily human-like in its appearance, speech, mannerisms, and even movement. However its
        artificial intelligence isn't the same as that of humans. Not yet. It doesn't have sentience or self-awareness
        or consciousness.
        <br />
        <br />
        Many neuroscientists believe that we won't ever reach the point of creating artificial human intelligence. "At
        the end of the day, AI comes down to 1's and 0's, while the human brain does not. We'll never see AI that is
        identical to that of humans."
      </Typography>
    ),
  }),
  [LiteratureName.SyntheticMuscles]: new Literature({
    title: "Synthetic Muscles",
    filename: LiteratureName.SyntheticMuscles,
    text: (
      <Typography>
        Initial versions of synthetic muscles weren't made of anything organic but were actually crude devices made to
        mimic human muscle function. Some of the early iterations were actually made of common materials such as fishing
        lines and sewing threads due to their high strength for a cheap cost.
        <br />
        <br />
        As technology progressed, however, advances in biomedical engineering paved the way for a new method of creating
        synthetic muscles. Instead of creating something that closely imitated the functionality of human muscle,
        scientists discovered a way of forcing the human body itself to augment its own muscle tissue using both
        synthetic and organic materials. This is typically done using gene therapy or chemical injections.
      </Typography>
    ),
  }),
  [LiteratureName.TensionsInTechRace]: new Literature({
    title: "Tensions rise in global tech race",
    filename: LiteratureName.TensionsInTechRace,
    factionRumors: [FactionName.OmniTekIncorporated, FactionName.MegaCorp, FactionName.ECorp],
    text: (
      <Typography>
        Have we entered a new Cold War? Is WWIII just beyond the horizon?
        <br />
        <br />
        After rumors came out that {CompanyName.OmniTekIncorporated} had begun developing advanced robotic
        supersoldiers, geopolitical tensions quickly flared between the USA, Russia, and several Asian superpowers. In a
        rare show of cooperation between corporations, {CompanyName.MegaCorp} and {CompanyName.ECorp} have reportedly
        launched hundreds of new surveillance and espionage satellites. Defense contractors such as{" "}
        {CompanyName.DeltaOne} and {CompanyName.AeroCorp} have been working with the CIA and NSA to prepare for
        conflict. Meanwhile, the rest of the world sits in earnest hoping that it never reaches full-scale war. With
        today's technology and firepower, a World War would assuredly mean the end of human civilization.
      </Typography>
    ),
  }),
  [LiteratureName.CostOfImmortality]: new Literature({
    title: "The Cost of Immortality",
    filename: LiteratureName.CostOfImmortality,
    text: (
      <Typography>
        Evolution and advances in medical and augmentation technology has lead to drastic improvements in human
        mortality rates. Recent figures show that the life expectancy for humans that live in a first-world country is
        about 130 years of age, almost double of what it was at the turn of the century. However, this increase in
        average lifespan has had some significant effects on society and culture.
        <br />
        <br />
        Due to longer lifespans and a better quality of life, many adults are holding off on having kids until much
        later. As a result, the percentage of youth in first-world countries has been decreasing, while the number of
        senior citizens is significantly increasing.
        <br />
        <br />
        Perhaps the most alarming result of all of this is the rapidly shrinking workforce. Despite the increase in life
        expectancy, the typical retirement age for workers in America has remained about the same, meaning a larger and
        larger percentage of people in America are retirees. Furthermore, many young adults are holding off on joining
        the workforce because they feel that they have plenty of time left in their lives for employment, and want to
        "enjoy life while they're young." For most industries, this shrinking workforce is not a major issue as most
        things are handled by robots anyways. However, there are still several key industries such as engineering and
        education that have not been automated, and these remain in danger to this cultural phenomenon.
      </Typography>
    ),
  }),
  [LiteratureName.TheHiddenWorld]: new Literature({
    title: "The Hidden World",
    filename: LiteratureName.TheHiddenWorld,
    factionRumors: [FactionName.Illuminati],
    text: (
      <Typography>
        WAKE UP SHEEPLE
        <br />
        <br />
        THE GOVERNMENT DOES NOT EXIST. CORPORATIONS DO NOT RUN SOCIETY
        <br />
        <br />
        THE {FactionName.Illuminati.toUpperCase()} ARE THE SECRET RULERS OF THE WORLD!
        <br />
        <br />
        Yes, the {FactionName.Illuminati} of legends. The ancient secret society that controls the entire world from the
        shadows with their invisible hand. With their personal wealth and skills they have penetrated every major
        government, financial agency, and corporation in the last three hundred years.
        <br />
        <br />
        OPEN YOUR EYES
        <br />
        <br />
        It was the {FactionName.Illuminati} that brought an end to democracy in the world. They are the driving force
        behind everything that happens.
        <br />
        <br />
        THEY ARE ALL AROUND YOU
        <br />
        <br />
        After destabilizing the world's governments, they are now entering the final stage of their master plan. They
        will secretly initiate global crises. Terrorism. Pandemics. World War. And out of the chaos that ensues they
        will build their New World Order.
      </Typography>
    ),
  }),
  [LiteratureName.TheNewGod]: new Literature({
    title: "The New God",
    filename: LiteratureName.TheNewGod,
    factionRumors: [FactionName.ChurchOfTheMachineGod],
    text: (
      <Typography>
        Everyone has a moment in their life when they wonder about the bigger questions.
        <br />
        <br />
        What's the point of all this? What is my purpose?
        <br />
        <br />
        Some people dare to think even bigger.
        <br />
        <br />
        What will the fate of the human race be?
        <br />
        <br />
        We live in an era vastly different from that of 15 or even 20 years ago. We have gone beyond the limits of
        humanity. We have stripped ourselves of the tyranny of flesh.
        <br />
        <br />
        The Singularity is here. The merging of man and machine. This is where humanity evolves into something greater.
        This is our future.
        <br />
        <br />
        Embrace it, and you will obey a new god. The God in the Machine.
      </Typography>
    ),
  }),
  [LiteratureName.NewTriads]: new Literature({
    title: "The New Triads",
    filename: LiteratureName.NewTriads,
    factionRumors: [FactionName.Tetrads],
    text: (
      <Typography>
        The Triads were an ancient transnational crime syndicate based in China, Hong Kong, and other Asian territories.
        They were often considered one of the first and biggest criminal secret societies. While most of the branches of
        the Triads have been destroyed over the past few decades, the crime faction has spawned and inspired a number of
        other Asian crime organizations over the past few years. The most notable of these is the {FactionName.Tetrads}.
        <br />
        <br />
        It is widely believed that the {FactionName.Tetrads} are a rogue group that splintered off from the Triads
        sometime in the mid 21st century. The founders of the {FactionName.Tetrads}, all of whom were ex-Triad members,
        believed that the Triads were losing their purpose and direction. The {FactionName.Tetrads} started off as a
        small group that mainly engaged in fraud and extortion. They were largely unknown until just a few years ago
        when they took over the illegal drug trade in all of the major Asian cities. They quickly became the most
        powerful crime syndicate in the continent.
        <br />
        <br />
        Not much else is known about the {FactionName.Tetrads}, or about the efforts the Asian governments and
        corporations are making to take down this large new crime organization. Many believe that the{" "}
        {FactionName.Tetrads} have infiltrated the governments and powerful corporations in Asia, which has helped
        facilitate their recent rapid rise.
      </Typography>
    ),
  }),
  [LiteratureName.TheSecretWar]: new Literature({
    title: "The Secret War",
    filename: LiteratureName.TheSecretWar,
    text: <Typography></Typography>,
  }),
  [LiteratureName.ABriefHistoryOfTranshumanism]: new Literature({
    title: "A Brief History of Transhumanism",
    filename: LiteratureName.ABriefHistoryOfTranshumanism,
    text: (
      <Typography>
        Human augmentation has come a long way since the first prosthetics and implants. Now any and all parts of the
        human body are able to be improved by technology: strength, speed, perception, intelligence. A large variety of
        powerful augmentations have been developed, but for most they may as well not exist. How did things end up like
        this?
        <br />
        <br />
        Part of the answer is, unfortunately, that the exact technology that has allowed us to come so far in human
        augmentation is also responsible for its great expense and exclusivity.
        <br />
        The early days of human augmentation were plagued with issues. Immunosuppressants were needed to keep people's
        bodies from rejecting the foreign bodies being implanted, with the body constantly trying to "restore" itself to
        its natural state. On top of that, with the messy nature of biology any replacement for a natural organ would
        fall short of full functionality. The skeleton doesn't just provide structural support and anchors for muscles;
        it also helps produce the blood in your body in the marrow. Even the fat in the body is a living organ that
        produces hormones helping to regulate its activity.
        <br />
        <br />
        Thus, the process was limited by the capacity of the human body to accept and deal with these changes. Biology
        is redundant and flexible, so replacing a few bones won't impact the body's functioning too badly. But every
        addition and change would add up, ultimately severely limiting the body's capacity for augmentation.
        <br />
        <br />
        Ultimately it was {CompanyName.VitaLife} who found a way around this limitation, though only at great expense...
        They discovered that by using a radical new form of gene therapy to reshape a person's body to not only
        accommodate but integrate the augmentations into their body at a cellular level, they could completely eliminate
        the side-effects of augmenting.
        <br />
        <br />
        The downside, of course, is the extreme expense and complexity of the process. Not only must each augment be
        tailored for its intended recipient and a custom genetic therapy program developed, but to truly avoid any
        side-effects this program must take into account the interactions between augmentations when multiple are
        installed. The combinatoric explosion in complexity leads to exponentially increasing costs when more than one
        augment is installed at the same time. Despite this most still prefer to install as many as possible at once,
        because in addition the radical transformation their body undergoes leaves them relearning to use their body for
        weeks or even months afterward.
        <br />
        <br />
        In the end humanity is left with the theoretical capacity to transform themselves to the limits of their
        imagininations, but the practical reality that the expense of even basic augmentations is beyond 95% of the
        population.
      </Typography>
    ),
  }),
  [LiteratureName.DarknetHandbook]: new Literature({
    title: LiteratureName.DarknetHandbook,
    filename: LiteratureName.DarknetHandbook,
    text: (
      <Typography>
        There is a legend of a powerful augment, known as the <span style={{ color: "red" }}>"Red Pill"</span>, that can
        only be found deep within the darknet. The mysterious faction {FactionName.Daedalus} has been searching for it
        for years, hoping to monopolize it one day.
        <br />
        <br />
        However, the legendary augment is hidden in the depths of a labyrinth. You will need to delve far into the dark
        in order to find these mysterious servers, and defeat their protections to gain their secret augments.
        <br />
        <br />
        The darknet itself is an extremely unstable network of servers. They will continually shift locations, restart,
        or even go offline. Some parts of the net are islands that can only be reached by riding on a moving server. In
        addition, these darknet servers cannot be accessed from a distance: you must build a script that can copy itself
        - or bring code along from home - in order to progress into the deeper layers of the 'net.
        <br />
        <br />
        You now have permanent access to the Darknet Navigator, allowing exploration of the 'net manually via the UI.
        But beware: the labyrinths further into the deep are said to only be accessible via script! Do you have the
        charisma and the scripting skills needed to conquer the dark?
        <br />
        <br />
        For more details on the darknet and its API, see the Darknet page under Documentation {">"} Advanced.
      </Typography>
    ),
  }),
  [LiteratureName.CacheHint1]: new Literature({
    title: "eGeoCacheing?",
    filename: LiteratureName.CacheHint1,
    text: <Typography>I've heard there are valuable .cache files to find out in the dark net.</Typography>,
  }),
  [LiteratureName.CacheHint2]: new Literature({
    title: "Cache the Flag",
    filename: LiteratureName.CacheHint2,
    text: <Typography>I ran this .cache file I found and it had crazy stuff in it!</Typography>,
  }),
  [LiteratureName.ServerOfflineHint]: new Literature({
    title: "Server offline again",
    filename: LiteratureName.ServerOfflineHint,
    text: (
      <Typography>
        My scripts went down again when their server went offline. I'll have to do something about that.
      </Typography>
    ),
  }),
  [LiteratureName.DarkWebRebootHint]: new Literature({
    title: "Darkweb server rebooted",
    filename: LiteratureName.DarkWebRebootHint,
    text: <Typography>Darkweb servers are known to reboot sometimes, requiring scripts to be restarted.</Typography>,
  }),
  [LiteratureName.PasswordServerHint]: new Literature({
    title: "Partial Password Jutsu",
    filename: LiteratureName.PasswordServerHint,
    text: (
      <Typography>
        There is a type of server that will tell you if you get some parts of the password correct.
      </Typography>
    ),
  }),
  [LiteratureName.TimingServerHint]: new Literature({
    title: "Timing Attack",
    filename: LiteratureName.TimingServerHint,
    text: (
      <Typography>
        I found a server that takes much longer to respond if you get some characters in the password correct.
      </Typography>
    ),
  }),
  [LiteratureName.BinaryServerHint]: new Literature({
    title: "Raw Data?",
    filename: LiteratureName.BinaryServerHint,
    text: <Typography>Some servers only respond with raw binary data. I wonder what each bit represents?</Typography>,
  }),
  [LiteratureName.DogNameHint]: new Literature({
    title: "Dog Name Ideas",
    filename: LiteratureName.DogNameHint,
    text: <Typography>What should I name my dog? Maybe {dogNameDictionary.join(", ")}?</Typography>,
  }),
  [LiteratureName.FactoryDefaultHint]: new Literature({
    title: "Factory Default",
    filename: LiteratureName.FactoryDefaultHint,
    text: <Typography>The factory default is usually one of {defaultSettingsDictionary.join(", ")}.</Typography>,
  }),
  [LiteratureName.StasisLinkHint]: new Literature({
    title: "Try the best new thing in web surfing: the Stasis Link!",
    filename: LiteratureName.StasisLinkHint,
    text: (
      <Typography>
        Tired of the server you are on restarting or moving? You need to try our latest networking tool, the Stasis
        Link! <br />
        With the click of a `ns.dnet.setStasisLink()`, you, too, can sleep soundly knowing that that server is not gonna
        go anywhere. <br />
        <br />
        Limited time only! While `ns.dnet.getStasisLinkLimit()` lasts!
      </Typography>
    ),
  }),
  [LiteratureName.LabHint]: new Literature({
    title: "There's something out there",
    filename: LiteratureName.LabHint,
    text: (
      <Typography>
        If you go deep enough into the dark net, they say there's a lost server out there with special files on it. I
        wonder how you can get there? It may even be deeper than the airgaps around this IP block...
        <br />
        <br />
        If I ever find it, I'll set down a stasis link next to it and charge tickets for admission!
      </Typography>
    ),
  }),
};
